import React, { useState, useEffect, useRef, useContext } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import moment from "moment";
import { Ucontext } from "../context/Ucontext";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllLandlords } from "../context/functions/AdminFunction";
import ActivityMetrics from "./landlord-metrics/components/ActivityMetrics";
import LandlordMetricsTable from "./landlord-metrics/components/LandlordMetricsTable";
import TopLandlords from "./landlord-metrics/components/TopLandlords";
import LandlordDetailsModal from "./landlord-metrics/components/LandlordDetailsModal";
import { useLandlordMetricsData } from "./landlord-metrics/hooks/useLandlordMetricsData";
import { useLandlordSearch } from "./landlord-metrics/hooks/useLandlordSearch";
import { useLandlordDetailMetrics } from "./landlord-metrics/hooks/useLandlordDetailMetrics";
import "../styles/landlord-metrics.css";
import "../styles/tenants.css";
import { X, Search } from "lucide-react";
import { constants } from "../utils/constants";

const LandlordMetrics = () => {
  const {
    getWorkspaceMetrics,
    getWorkspaceMetricsFullInfo,
    getLandlordActivityMetrics,
    getLandlordFunnelMetrics,
  } = useContext(Ucontext);

  // Filter State
  const [filters, setFilters] = useState({
    datePreset: "",
    startDate: null,
    endDate: null,
    landlordId: "",
    landlordSearch: "",
    selfServing: "",
    landlordType: ""
  });
  const [showDateRangeMenu, setShowDateRangeMenu] = useState(false);
  const [showLandlordTypeMenu, setShowLandlordTypeMenu] = useState(false);

  // Data State
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalRegistrations: 0,
    totalTenants: 0,
    totalLandlords: 0,
    avgTenantsPerLandlord: 0,
    mostActiveWorkspace: "",
  });
  const [landlordMetrics, setLandlordMetrics] = useState([]);
  const [topLandlords, setTopLandlords] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [landlordMetricsList, setLandlordMetricsList] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(constants.LISTING_ROWLIMIT);

  // Landlord Search State
  const [landlordSearchResults, setLandlordSearchResults] = useState([]);
  const [searchingLandlords, setSearchingLandlords] = useState(false);
  const [showLandlordDropdown, setShowLandlordDropdown] = useState(false);

  // Loading State
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // URL Parameters
  const [searchParams] = useSearchParams();
  const landlordIdFromUrl = searchParams.get("landlordId");

  // Modal State
  const [showModal, setShowModal] = useState(false);

  // AbortController for canceling requests
  const abortControllerRef = useRef(null);
  const dateRangeRef = useRef(null);
  const landlordTypeRef = useRef(null);

  // Use custom hooks
  const { fetchSummaryMetrics, fetchLandlordMetrics } = useLandlordMetricsData(
    filters,
    getWorkspaceMetrics,
    getWorkspaceMetricsFullInfo,
    getLandlordActivityMetrics,
    getLandlordFunnelMetrics,
    setSummaryMetrics,
    setLandlordMetrics,
    setTopLandlords,
    abortControllerRef,
    setLoadingSummary,
    setLoadingTable
  );

  const {
    searchRef,
    handleSearchFocus,
    handleSearchClick,
    handleSearchChange,
    handleSelectLandlord,
    clearLandlordFilter,
  } = useLandlordSearch(
    filters,
    landlordSearchResults,
    setFilters,
    setLandlordSearchResults,
    setShowLandlordDropdown,
    setSearchingLandlords,
    abortControllerRef
  );

  const { fetchLandlordDetailMetrics } = useLandlordDetailMetrics(
    filters,
    getWorkspaceMetricsFullInfo,
    setLandlordMetricsList,
    setLoadingDetails
  );

  const handleFilterChange = (name, value) => {
    if (name === "datePreset") {
      if (!value) {
        setFilters((prev) => ({
          ...prev,
          datePreset: "",
          startDate: "",
          endDate: "",
        }));
        return;
      }
      if (value === "custom") {
        setFilters((prev) => ({
          ...prev,
          datePreset: "custom",
          startDate: "",
          endDate: "",
        }));
        return;
      }
      const startDate = moment().startOf(value).format("YYYY-MM-DD");
      const endDate = moment().endOf(value).format("YYYY-MM-DD");
      setFilters((prev) => ({
        ...prev,
        datePreset: value,
        startDate,
        endDate,
      }));
      return;
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleRefresh = () => {
    fetchSummaryMetrics();
    fetchLandlordMetrics();
  };

  const getDateRangeLabel = () => {
    if (filters.datePreset === "") return "All Time";
    if (filters.datePreset === "month") return "Month";
    if (filters.datePreset === "custom") return "Custom";
    return "Week";
  };
  
  const getLandlordTypeLabel = () => {
    if (filters.landlordType === "") return "All Types";
    if (filters.landlordType === "retail") return "Retail";
    if (filters.landlordType === "institutional") return "Institutional";
    return filters.landlordType;
  };

  const handleViewDetails = async (metric) => {
    setSelectedLandlord(metric);
    setShowModal(true);
    await fetchLandlordDetailMetrics(metric.landlord?._id, metric.workspaceId);
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowLandlordDropdown(false);
      }
      if (
        dateRangeRef.current &&
        !dateRangeRef.current.contains(event.target)
      ) {
        setShowDateRangeMenu(false);
      }
      if (
        landlordTypeRef.current &&
        !landlordTypeRef.current.contains(event.target)
      ) {
        setShowLandlordTypeMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Refetch detail data when page changes
  useEffect(() => {
    if (showModal && selectedLandlord) {
      fetchLandlordDetailMetrics(selectedLandlord.landlord?._id, selectedLandlord.workspaceId);
    }
  }, [showModal, selectedLandlord, fetchLandlordDetailMetrics]);

  // Load initial data on mount and when filters change
  useEffect(() => {
    fetchSummaryMetrics();
    fetchLandlordMetrics();
  }, [filters.startDate, filters.endDate, filters.landlordId, filters.selfServing, filters.landlordType]); // Re-fetch when date range, landlord, selfServing, or landlordType changes

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.startDate, filters.endDate, filters.landlordId, filters.selfServing, filters.landlordType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Set page title
  useEffect(() => {
    document.title = "Landlord Metrics | Admin";
  }, []);

  // Handle landlordId from URL parameters
  useEffect(() => {
    if (landlordIdFromUrl) {
      setFilters(prev => ({
        ...prev,
        landlordId: landlordIdFromUrl
      }));

      // Fetch landlord details to populate the search field
      const fetchLandlordDetails = async () => {
        try {
          const res = await getAllLandlords(1, undefined, undefined, landlordIdFromUrl);
          if (res && res.landlords && res.landlords.length > 0) {
            const landlord = res.landlords[0];
            setFilters(prev => ({
              ...prev,
              landlordSearch: `${landlord.fname_companyName} ${landlord.lname}`.trim() || landlord.email
            }));
            setLandlordSearchResults([landlord]);
          }
        } catch (err) {
          console.error("Error fetching landlord details:", err);
        }
      };

      fetchLandlordDetails();
    }
  }, [landlordIdFromUrl]);

  return (
    <div className="landlord-metrics-container">
      <div className="d-flex flex-column justify-content-between align-items-start mb-4">
        <div className="mb-4">
          <h4
            className="fw-bold mb-1" 
            style={{ color: "#2c3e50", fontSize: "22px" }}
          >
            Landlord Metrics
          </h4>
          <p className="text-muted small fw-medium mb-0">
            Track new tenant registrations by landlord and workspace
          </p>
        </div>

        <div className="metrics-top-filters-container">
          <div className="metrics-top-filter-item" ref={dateRangeRef}>
            <div className="range-selector">
              <span className="range-label">Range</span>
              <button
                type="button"
                className="range-chip"
                onClick={() => setShowDateRangeMenu((prev) => !prev)}
              >
                {getDateRangeLabel(filters.datePreset)}
              </button>
              {showDateRangeMenu && (
                <div className="range-menu">
                  {[
                    { value: "", label: "All Time" },
                    { value: "week", label: "Week" },
                    { value: "month", label: "Month" },
                    { value: "custom", label: "Custom" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`range-menu-item ${
                        filters.datePreset === option.value
                          ? "range-menu-item-active"
                          : ""
                      }`}
                      onClick={() => {
                        handleFilterChange("datePreset", option.value);
                        setShowDateRangeMenu(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {filters.datePreset === "custom" && (
              <div className="custom-dates">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                  className="date-input"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                  className="date-input"
                />
              </div>
            )}
          </div>

          <div className="metrics-top-filter-item" ref={landlordTypeRef}>
            <div className="range-selector">
              <span className="range-label">Type</span>
              <button
                type="button"
                className="range-chip"
                onClick={() => setShowLandlordTypeMenu((prev) => !prev)}
              >
                {getLandlordTypeLabel(filters.landlordType)}
              </button>
              {showLandlordTypeMenu && (
                <div className="range-menu">
                  {[
                    { value: "", label: "All Types" },
                    { value: "retail", label: "Retail" },
                    { value: "institutional", label: "Institutional" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`range-menu-item ${
                        filters.landlordType === option.value
                          ? "range-menu-item-active"
                          : ""
                      }`}
                      onClick={() => {
                        handleFilterChange("landlordType", option.value);
                        setShowLandlordTypeMenu(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ActivityMetrics
        summaryMetrics={summaryMetrics}
        loadingSummary={loadingSummary}
      />

      <Row className="mb-4">
        {/* <Col lg={8}> */}
          <LandlordMetricsTable
            landlordMetrics={landlordMetrics}
            loadingTable={loadingTable}
            onViewDetails={handleViewDetails}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            filters={filters}
            handleSearchChange={handleSearchChange}
            handleSearchFocus={handleSearchFocus}
            handleSearchClick={handleSearchClick}
            handleSelectLandlord={handleSelectLandlord}
            clearLandlordFilter={clearLandlordFilter}
            showLandlordDropdown={showLandlordDropdown}
            landlordSearchResults={landlordSearchResults}
            searchingLandlords={searchingLandlords}
            searchRef={searchRef}
            handleFilterChange={handleFilterChange}
          />
        {/* </Col> */}
      </Row>

      <LandlordDetailsModal
        showModal={showModal}
        selectedLandlord={selectedLandlord}
        landlordMetricsList={landlordMetricsList}
        loadingDetails={loadingDetails}
        onHide={() => setShowModal(false)}
      />
    </div>
  );
};

export default LandlordMetrics;